/* global gtag */

import { campaignIconsTapAction } from "@/modules/actions/campaignIconsTapAction";
import { icons, setupVideoIntersectionObserver } from "@/modules/parts/icons.js";
import apiRepositories from '@/plugins/apiRepositories'
import { isStoreeelGaConsoleEnabled, isStoreeelPreviewMode, trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { cvProcess } from "@/helpers/cvProcess";
import { shouldLoadGtag } from "@/helpers/gtagLoading";
import { saveCrossDomainCvParamsToLocalStorage, stripCrossDomainCvParams } from "@/helpers/crossDomainCv";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { enableMouseDragScroll } from "@/modules/actions/mobile/enableMouseDragScroll";
import { enableCampaignAutoScroll } from "@/modules/actions/enableCampaignAutoScroll";
import { AUTO_SCROLL_MODE, CUSTOM_HTML_POSITION, getCampaignAutoScrollMode, getCampaignCustomHtmlPosition, getCampaignGroupGap, getCampaignTileBlockMaxWidth, getCampaignTileGapVertical, getRenderedCampaignGroupGap } from "@/helpers/campaignSettings";
import { openSharedContent } from "@/helpers/openSharedContent";
import { getShareParams, stripShareParams } from "@/helpers/shareLink";
import { rememberPreparedCampaignForDisplay } from '@/helpers/preparedCampaignDisplay';
import { normalizeStreamsTargeting, prepareCampaignForDisplay, restoreTargetingAnswersFromStorage } from '@/helpers/streamsTargeting';
import {
  createStoreeelCampaignHost,
  createStoreeelShadowRootContainer,
  isStoreeelShadowDomEnabled,
} from '@/helpers/storeeelShadowDom';
import {
  activateInsertPointSelector,
  activateInsertPointSelectorDeviceWarning,
  buildInsertPointRegistrationPayload,
  buildInitialCampaignSettings,
  buildInsertPointSelectionXPath,
  getCampaignFrameFromSelectorFrame,
  getCurrentInsertPointSelectorDevice,
  getInsertPointSelectorDevice,
  getInsertPointSelectorFrame,
  getInsertPointSelectorToken,
  isInsertPointSelectorEnabled,
  isInsertPointSelectorDeviceMismatch,
  resolveElementByXPath,
  resolveInsertPointSelectionElement,
} from '@/helpers/insertPointSelector/index.js';

const GLOBAL_INIT_STATE_KEY = '__storeeelLibraryInitState__';

const normalizeCurrentUrl = () => {
  const url = stripCrossDomainCvParams(stripShareParams(window.location.href).toString());
  if (url.searchParams.has('_storeeel_preview_campaign_id')) {
    url.searchParams.delete('_storeeel_preview_campaign_id');
  }
  return url.toString();
};

const getGlobalInitState = () => {
  // SPAでタグが複数回読み込まれても初期化が重複しないようwindowに状態を保持する
  if (!window[GLOBAL_INIT_STATE_KEY]) {
    window[GLOBAL_INIT_STATE_KEY] = {
      instance: null,
      promise: null,
      initialized: false,
      items: null,
      tenantId: null,
      currentUrl: null,
      historyPatched: false,
      insertPointSelectorPending: false,
      insertPointSelectorMode: false,
      historyListenersBound: false,
      domObserverBound: false,
      domObserver: null,
      retryTimerId: null,
      trackingEnabled: false,
      displayOpportunityTrackedCampaignIds: new Set(),
      viewTrackedCampaignIds: new Set(),
    };
  }
  if (!(window[GLOBAL_INIT_STATE_KEY].displayOpportunityTrackedCampaignIds instanceof Set)) {
    window[GLOBAL_INIT_STATE_KEY].displayOpportunityTrackedCampaignIds = new Set();
  }
  if (!(window[GLOBAL_INIT_STATE_KEY].viewTrackedCampaignIds instanceof Set)) {
    window[GLOBAL_INIT_STATE_KEY].viewTrackedCampaignIds = new Set();
  }
  return window[GLOBAL_INIT_STATE_KEY];
};

const formatInsertPointRegistrationError = (error) => {
  const status = error?.response?.status;
  const responseData = error?.response?.data;
  const responseError = typeof responseData?.error === 'string' ? responseData.error : null;
  const responseErrors = Array.isArray(responseData?.errors) ? responseData.errors.filter(Boolean) : [];

  if (status === 403) {
    return responseError || '差し込み位置を登録できませんでした。トークンのテナントが一致しません。';
  }
  if (status === 409) {
    return responseError || '差し込み位置を登録できませんでした。トークンの有効期限切れ、または使用済みです。';
  }
  if (status === 422) {
    if (responseErrors.length > 0) {
      return `差し込み位置を登録できませんでした。${responseErrors.join(' / ')}`;
    }
    return responseError || '差し込み位置を登録できませんでした。selector が不正です。';
  }
  if (responseError) return responseError;
  if (responseErrors.length > 0) return responseErrors.join(' / ');
  return '差し込み位置の登録に失敗しました。';
};

const normalizeCampaignId = (value) => (value == null ? '' : String(value));

const campaignIdentifierValues = (campaign) => [
  campaign?.id,
  campaign?.campaignId,
  campaign?.campaign_id,
  campaign?.hashid,
  campaign?.hashId,
  campaign?.customId,
  campaign?.custom_id,
]
  .map(normalizeCampaignId)
  .filter(Boolean);

const campaignMatchesPreviewId = (campaign, previewCampaignId) => (
  campaignIdentifierValues(campaign).includes(normalizeCampaignId(previewCampaignId))
);

const isExpectedPreviewCampaignFrame = (campaign, expectedCampaignFrame) => (
  campaign?.frame === expectedCampaignFrame
);

const isSelectablePreviewCampaign = (campaign, expectedCampaignFrame) => (
  isExpectedPreviewCampaignFrame(campaign, expectedCampaignFrame)
  && Array.isArray(campaign?.contentGroups)
  && campaign.contentGroups.length > 0
);

const prepareInsertPointSelectorPreviewCampaign = (campaign, {
  tenantId,
  currentUrl,
}) => {
  if (!campaign) return null;

  const storedSegments = localStorageRepositories.get('targetingSegment')?.get?.(tenantId) || {};
  const savedAnswers = restoreTargetingAnswersFromStorage(campaign, {
    segments: storedSegments,
  });
  const displayItem = prepareCampaignForDisplay(campaign, savedAnswers, {
    tenantId,
    currentUrl,
  });

  if (!Array.isArray(displayItem?.contentGroups) || displayItem.contentGroups.length === 0) {
    return null;
  }

  return displayItem;
};

const buildInsertPointSelectorPreviewCampaign = (items, {
  tenantId,
  currentUrl,
  selectorFrame,
  previewCampaignId = null,
  preferredCampaign = null,
}) => {
  const expectedCampaignFrame = getCampaignFrameFromSelectorFrame(selectorFrame);
  const campaigns = Array.isArray(items?.data) ? items.data : [];
  const normalizedPreviewCampaignId = normalizeCampaignId(previewCampaignId);

  if (normalizedPreviewCampaignId) {
    const matchedCampaign = campaigns.find((item) => (
      campaignMatchesPreviewId(item, normalizedPreviewCampaignId)
        && isExpectedPreviewCampaignFrame(item, expectedCampaignFrame)
    ));

    // 明示されたキャンペーンIDがある場合は、別キャンペーンへフォールバックしない。
    return prepareInsertPointSelectorPreviewCampaign(matchedCampaign, {
      tenantId,
      currentUrl,
    });
  }

  const preferredCandidates = [
    preferredCampaign,
    window.storeeelItems,
    window.storeeelOriginalItems,
  ].filter((campaign) => isSelectablePreviewCampaign(campaign, expectedCampaignFrame));

  const previewCampaign = preferredCandidates[0]
    || campaigns.find((item) => isSelectablePreviewCampaign(item, expectedCampaignFrame));

  return prepareInsertPointSelectorPreviewCampaign(previewCampaign, {
    tenantId,
    currentUrl,
  });
};

const trackCampaignViewWhenVisible = (campaignElement, item, trackedCampaignIds) => {
  if (!campaignElement || campaignElement.dataset.storeeelViewObserved === 'true') return;
  const campaignId = String(item?.id ?? '');
  if (!campaignId || trackedCampaignIds.has(campaignId)) return;

  // 画面内に入った時だけ1回送る
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      observer.unobserve(campaignElement);
      observer.disconnect();

      if (campaignElement.dataset.storeeelViewTracked === 'true') return;
      campaignElement.dataset.storeeelViewTracked = 'true';
      if (trackedCampaignIds.has(campaignId)) return;
      trackedCampaignIds.add(campaignId);

      trackStoreeelEvent('storeeel_campaign_view', {
        'storeeel_name': item.name,
        'campaign_id': item.id,
      });
    });
  }, {
    root: null,
    threshold: 0.01,
  });

  campaignElement.dataset.storeeelViewObserved = 'true';
  observer.observe(campaignElement);
};

const trackCampaignDisplayOpportunity = (item, trackedCampaignIds) => {
  const campaignId = String(item?.id ?? '');
  if (!campaignId || trackedCampaignIds.has(campaignId)) return;

  trackedCampaignIds.add(campaignId);
  trackStoreeelEvent('storeeel_campaign_display_ready', {
    'storeeel_name': item.name,
    'campaign_id': item.id,
  });
};

export class reelLibrary {
  constructor(tagParams) {
    this.api = apiRepositories(tagParams)
    this.tagParams = tagParams
    // 戻る操作（BFCache復帰 / SPAのpopstate等）でページ側DOMが差し替わると、
    // libraryの初期化が再実行されずキャンペーンが消えることがあるため、
    // 「キャンペーンDOMが無ければ再描画する」ための制御を持たせる。
    this._execPromise = null;
    this._insertPointSelectorCleanup = null;
    this._setupAutoReexecOnHistory();
  }
  async exec() {
    const globalState = getGlobalInitState();
    const tenantId = this.tagParams?.tenantId ?? null;
    const currentUrl = normalizeCurrentUrl();
    const insertPointSelectorMode = isInsertPointSelectorEnabled(this.tagParams);
    window.storeeelCurrentUrl = currentUrl;
    const shouldResetCache = globalState.initialized
      && (globalState.tenantId !== tenantId || globalState.currentUrl !== currentUrl);

    if (shouldResetCache) {
      globalState.instance = null;
      globalState.promise = null;
      globalState.initialized = false;
      globalState.items = null;
      globalState.tenantId = null;
      globalState.currentUrl = null;
      globalState.insertPointSelectorPending = false;
      globalState.insertPointSelectorMode = false;
      globalState.trackingEnabled = false;
      globalState.displayOpportunityTrackedCampaignIds = new Set();
      globalState.viewTrackedCampaignIds = new Set();
    }

    // 履歴イベント時は常に最新のインスタンスだけが再描画を担当する
    globalState.instance = this;

    if (globalState.promise) return globalState.promise;
    if (globalState.initialized) {
      if (globalState.items) {
        if (!this._hasMissingCampaignRendered(globalState.items) && this._hasAnyCampaignRendered()) {
          return Promise.resolve();
        }
        const renderPromise = this._renderCampaigns(globalState.items, { track: globalState.trackingEnabled })
          .finally(() => {
            if (globalState.promise === renderPromise) {
              globalState.promise = null;
            }
          });
        globalState.promise = renderPromise;
        return renderPromise;
      }
      return Promise.resolve();
    }

    this.api = apiRepositories(this.tagParams);

    // 同一インスタンスでの多重実行を抑止（イベント二重登録・DOM二重挿入の防止）
    if (this._execPromise) return this._execPromise;
    const execPromise = this._execInternal()
      .then((items) => {
        globalState.items = items;
        globalState.initialized = true;
        globalState.tenantId = tenantId;
        globalState.currentUrl = currentUrl;
        globalState.insertPointSelectorMode = insertPointSelectorMode;
        return items;
      })
      .catch((error) => {
        if (globalState.instance === this) {
          globalState.instance = null;
          globalState.initialized = false;
          globalState.items = null;
          globalState.tenantId = null;
          globalState.currentUrl = null;
          globalState.insertPointSelectorPending = false;
          globalState.insertPointSelectorMode = false;
          globalState.trackingEnabled = false;
          globalState.displayOpportunityTrackedCampaignIds = new Set();
          globalState.viewTrackedCampaignIds = new Set();
        }
        throw error;
      })
      .finally(() => {
        this._execPromise = null;
        if (globalState.promise === execPromise) {
          globalState.promise = null;
        }
      });
    this._execPromise = execPromise;
    globalState.promise = execPromise;
    return execPromise;
  }

  _setupAutoReexecOnHistory() {
    const globalState = getGlobalInitState();
    // pageshowはBFCache復帰時にも呼ばれるため、戻る操作での再表示に有効
    if (globalState.historyListenersBound) return;
    globalState.historyListenersBound = true;

    window.addEventListener('pageshow', () => this._scheduleEnsureRendered(false));
    window.addEventListener('popstate', () => this._scheduleEnsureRendered(true));
    window.addEventListener('hashchange', () => this._scheduleEnsureRendered(true));
    window.addEventListener('storeeel:locationchange', () => this._scheduleEnsureRendered(true));
    this._patchHistoryForSpa();
  }

  _setupAutoReexecOnDomMutations() {
    const globalState = getGlobalInitState();
    if (globalState.domObserverBound || typeof MutationObserver === 'undefined') return;
    globalState.domObserverBound = true;

    const observe = () => {
      if (globalState.domObserver || !document.body) return;
      globalState.domObserver = new MutationObserver(() => {
        const activeState = getGlobalInitState();
        if (!activeState.initialized || !activeState.items) return;
        if (!this._hasMissingCampaignRendered(activeState.items)) return;

        // カスタムHTMLで補っていた「DOM変化後にキャンペーンが無ければ再実行」をライブラリ側で行う
        this._scheduleEnsureRendered(true, 0);
      });

      globalState.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    };

    if (document.body) {
      observe();
    } else {
      document.addEventListener('DOMContentLoaded', observe, { once: true });
    }
  }

  _scheduleEnsureRendered(force = false, delayMs = 0) {
    const globalState = getGlobalInitState();
    if (globalState.retryTimerId) clearTimeout(globalState.retryTimerId);
    // SPAのDOM更新直後は挿入先も組み替わることがあるので、少し後で確認する
    globalState.retryTimerId = setTimeout(() => {
      globalState.retryTimerId = null;
      const activeInstance = getGlobalInitState().instance;
      if (!activeInstance) return;
      activeInstance._ensureRenderedWithRetry(5, force);
    }, delayMs);
  }

  async _ensureRenderedWithRetry(remainingAttempts = 5, force = false) {
    const globalState = getGlobalInitState();
    // 非表示タブ中の処理は避ける（復帰後にpageshow/visibilitychange等で再評価される想定）
    if (document.hidden) return;
    if (globalState.insertPointSelectorMode) return;
    if (!force && this._hasAnyCampaignRendered()) return;

    try {
      // DOMが消えているケースでは「再初期化」ではなく「再描画」目的でexecを呼び直す
      await this.exec();
    } catch {
      // exec側でログ出力されるのでここでは握りつぶす
    }

    if (this._hasAnyCampaignRendered()) return;
    if (remainingAttempts <= 1) return;

    await new Promise(resolve => setTimeout(resolve, 200));
    return this._ensureRenderedWithRetry(remainingAttempts - 1, force);
  }

  _patchHistoryForSpa() {
    const globalState = getGlobalInitState();
    if (globalState.historyPatched) return;
    globalState.historyPatched = true;

    const wrap = (type) => {
      const original = history[type];
      history[type] = function (...args) {
        const result = original.apply(this, args);
        window.dispatchEvent(new Event('storeeel:locationchange'));
        return result;
      };
    };

    wrap('pushState');
    wrap('replaceState');
  }

  _hasAnyCampaignRendered() {
    // キャンペーン挿入ルート要素の有無で「描画済み」を判定する
    return !!document.querySelector('[data-storeeel-campaign-id]');
  }

  _hasMissingCampaignRendered(items) {
    // 一部のキャンペーンだけがReact等に削除された場合も、残っているDOMに惑わされず復元する
    return (items?.data || []).some((item) => {
      if (!Array.isArray(item?.contentGroups) || item.contentGroups.length === 0) return false;
      return !document.querySelector(`[data-storeeel-campaign-id="${item.id}"]`);
    });
  }

  async _execInternal() {
    // 動画情報を取得
    const urlParams = new URLSearchParams(window.location.search);
    const previewCampaignId = urlParams.get('_storeeel_preview_campaign_id');
    const isPreview = isStoreeelPreviewMode();
    const globalState = getGlobalInitState();
    globalState.trackingEnabled = !isPreview;
    // 本番bundleにテストfixtureを同梱しないため、テストデータはテストページから注入する
    const injectedTestMockData = window.__STOREEEL_TEST_OVERRIDE_MOCK_RESPONSE__
      ?? window.__STOREEEL_TEST_MOCK_DATA__
      ?? null;
    const items = injectedTestMockData
      ? injectedTestMockData
      : await this.api.get('streams').index({ ...this.api.commonRequestParams(), _storeeel_preview_campaign_id: previewCampaignId });
    window.storeeelTenantId = this.tagParams?.tenantId ?? 'default';
    window.storeeelData = items.data;
    const gtagId = items.meta?.gtag;
    window.storeeelGtag = gtagId;
    window.storeeelCrossDomainEnabled = items.meta?.crossDomainEnabled === true;
    window.isIphone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
    normalizeStreamsTargeting(items);
    window.storeeelData = items.data;

    // Google Analytics 読み込み（エラーをキャッチする）
    if (shouldLoadGtag({ gtagId, hasGtag: Boolean(window.gtag), isPreview })) {
      try {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.async = true;
          script.src = 'https://www.googletagmanager.com/gtag/js?id=' + gtagId;
          script.onload = () => {
            window.dataLayer = window.dataLayer || [];
            window.gtag = function () {
              window.dataLayer.push(arguments);
            };
            const shouldConsoleLogGa = isStoreeelGaConsoleEnabled();

            const jsDate = new Date();
            if (shouldConsoleLogGa) console.log('[StoreeelLibrary][GA4]', 'js', jsDate);
            gtag('js', jsDate);
            if (shouldConsoleLogGa) console.log('[StoreeelLibrary][GA4]', 'config', gtagId);
            gtag('config', gtagId);
            resolve();
          };
          script.onerror = (e) => {
            reject(e);
          };
          document.head.appendChild(script);
        });
      } catch (e) {
        console.warn('gtag.js の読み込みに失敗しました:', e);
      }
    }

    if (!injectedTestMockData && !isPreview) {
      if (window.storeeelCrossDomainEnabled) {
        saveCrossDomainCvParamsToLocalStorage();
      }
      // cv計測
      cvProcess(items.meta?.cvCampaignIds)
    }

    try {
      // localStorageのTTLが切れているものは削除
      localStorageRepositories.get('targetingSegment')?.removeExpired?.();
      localStorageRepositories.get('targetingQuestionnaireSkip')?.removeExpired?.();
    } catch (e) {
      console.warn('localStorageのTTL削除に失敗:', e);
    }

    if (isInsertPointSelectorEnabled(this.tagParams)) {
      globalState.insertPointSelectorPending = true;
      globalState.insertPointSelectorMode = true;

      if (this._insertPointSelectorCleanup) {
        this._insertPointSelectorCleanup();
        this._insertPointSelectorCleanup = null;
      }

      await new Promise((resolve) => {
        const completeInsertPointSelector = () => {
          globalState.insertPointSelectorPending = false;
          this._insertPointSelectorCleanup = null;
          resolve();
        };

        const startInsertPointSelector = (sourceItems = items) => {
          // 選択確定までは実描画せず、ページ上の差し込み位置だけ選べるようにする
          const selectorFrame = getInsertPointSelectorFrame();
          const previewCampaign = buildInsertPointSelectorPreviewCampaign(sourceItems, {
            tenantId: this.tagParams?.tenantId ?? 'default',
            currentUrl: window.storeeelCurrentUrl,
            selectorFrame,
            previewCampaignId,
            preferredCampaign: window.storeeelItems || window.storeeelOriginalItems || null,
          });
          const renderPreviewHtml = (settings) => icons({
            ...previewCampaign,
            ...settings,
            auto_scroll_mode: settings.autoScrollMode,
            loop_playback_enabled: settings.loopPlaybackEnabled,
            is_read_status_visible: settings.isReadStatusVisible,
            unread_color_code: settings.unreadColorCode,
            read_color_code: settings.readColorCode,
            read_status_border_width: settings.readStatusBorderWidth,
            thumbnail_scale: settings.thumbnailScale,
            group_gap: settings.groupGap,
            group_name_font_size: settings.groupNameFontSize,
            group_name_font_weight: settings.groupNameFontWeight,
            group_name_color_code: settings.groupNameColorCode,
            group_name_font_family: settings.groupNameFontFamily,
          });
          const initialSettings = buildInitialCampaignSettings(previewCampaign || {});
          const previewHtml = previewCampaign ? renderPreviewHtml(initialSettings) : '';
          const insertPointSelectorToken = getInsertPointSelectorToken(this.tagParams);
          this._insertPointSelectorCleanup = activateInsertPointSelector({
            frame: selectorFrame,
            previewHtml,
            previewCampaign,
            renderPreviewHtml,
            initialSettings,
            settingsToken: insertPointSelectorToken,
            onSelect: async (insertSelection, campaignSettings = {}) => {
              try {
                const selectionXPath = buildInsertPointSelectionXPath(insertSelection);
                if (selectionXPath) {
                  // 選択結果を保存処理へつなげやすいよう、統合済みXPathを出力する
                  console.log('[StoreeelLibrary] insert point xpath:', selectionXPath);
                }
                if (insertPointSelectorToken) {
                  const insertPointSelectorDevice = getInsertPointSelectorDevice();
                  const deviceCampaignSettings = {
                    isMobile: insertPointSelectorDevice === 'mobile',
                  };
                  const shouldSubmitCampaignSettings = insertPointSelectorDevice !== 'mobile';
                  const payload = buildInsertPointRegistrationPayload(
                    insertSelection,
                    insertPointSelectorToken,
                    shouldSubmitCampaignSettings
                      ? { ...campaignSettings, ...deviceCampaignSettings }
                      : deviceCampaignSettings
                  );
                  if (!payload) {
                    throw new Error('差し込み位置保存用の payload を生成できませんでした');
                  }
                  // 簡単差し込み確定時に、管理側で再利用できる XPath 情報を保存する
                  // tenant_id は simple-request化に伴いヘッダーではなく body で送る（storeeel-infra#2）
                  await this.api.get('insertPointRegistration').create({ ...payload, tenant_id: this.tagParams?.tenantId });
                  completeInsertPointSelector();
                  return {
                    message: '差し込み位置を登録しました。',
                  };
                }
                completeInsertPointSelector();
                return {
                  message: '差し込み位置を確定しました。',
                };
              } catch (error) {
                const message = formatInsertPointRegistrationError(error);
                console.warn('[StoreeelLibrary] insert point registration failed:', message, error);
                throw new Error(message);
              }
            },
          });
        };

        const refetchStreamsForCurrentDevice = async () => {
          if (injectedTestMockData) return items;
          try {
            const refreshedItems = await this.api.get('streams').index({ ...this.api.commonRequestParams(), _storeeel_preview_campaign_id: previewCampaignId });
            normalizeStreamsTargeting(refreshedItems);
            window.storeeelData = refreshedItems.data;
            window.storeeelGtag = refreshedItems.meta?.gtag;
            return refreshedItems;
          } catch (error) {
            console.warn('[StoreeelLibrary] insert point selector preview refetch failed:', error);
            return items;
          }
        };

        const expectedDevice = getInsertPointSelectorDevice();
        const currentDevice = getCurrentInsertPointSelectorDevice();
        if (isInsertPointSelectorDeviceMismatch(expectedDevice, currentDevice)) {
          // 指定デバイスと閲覧デバイスが異なる場合は、閉じる時点で一致していれば selector UI を起動する
          this._insertPointSelectorCleanup = activateInsertPointSelectorDeviceWarning({
            expectedDevice,
            onClose: async ({ isMatched } = {}) => {
              if (isMatched) {
                const refreshedItems = await refetchStreamsForCurrentDevice();
                startInsertPointSelector(refreshedItems);
                return;
              }
              completeInsertPointSelector();
            },
          });
          return;
        }

        startInsertPointSelector();
      });
      return items;
    }

    await this._renderCampaigns(items, { track: !isPreview });
    return items;
  }

  async _renderCampaigns(items, { track, insertSelection } = {}) {
    const FIND_SITE_TITLE_RETRY_ATTEMPTS = 20;
    const FIND_SITE_TITLE_RETRY_DELAY_MS = 500;
    const hasShareParams = Boolean(getShareParams());
    const tryOpenSharedContent = () => {
      if (!hasShareParams || window.__storeeelShareOpenAttempted) return;
      // 共有リンク対象のキャンペーンが描画できたタイミングで即オープンを試みる
      if (openSharedContent()) {
        window.__storeeelShareOpenAttempted = true;
      }
    };

    let selectedInsertAnchor = insertSelection
      ? resolveInsertPointSelectionElement(insertSelection)
      : null;

    const findSiteTitle = (item) => {
      if (insertSelection) return selectedInsertAnchor;
      if (item.insertElementKind === "id") {
        return document.querySelector(`#${item.insertElementValue}`);
      }
      if (item.insertElementKind === "class") {
        const elements = document.getElementsByClassName(item.insertElementValue);
        return elements[item.insertElementIndex];
      }
      if (item.insertElementKind === "xpath") {
        return resolveElementByXPath(item.insertElementValue);
      }
      return null;
    };

    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const findSiteTitleWithRetry = async (item, remainingAttempts = FIND_SITE_TITLE_RETRY_ATTEMPTS) => {
      const siteTitle = findSiteTitle(item);
      if (siteTitle) return siteTitle;
      if (remainingAttempts <= 1) return null;

      // SPAで挿入先DOMが後から生成されるケースを考慮し、キャンペーン単位で探索を再試行する
      await wait(FIND_SITE_TITLE_RETRY_DELAY_MS);
      return findSiteTitleWithRetry(item, remainingAttempts - 1);
    };

    const executeScriptsInCustomHtml = (root) => {
      const containers = root.matches('.storeeel-campaign-custom-html')
        ? [root]
        : Array.from(root.querySelectorAll('.storeeel-campaign-custom-html'));

      containers.forEach((container) => {
        const scripts = container.querySelectorAll('script:not([data-storeeel-executed])');
        scripts.forEach((originalScript) => {
          const executedScript = document.createElement('script');
          Array.from(originalScript.attributes).forEach((attr) => {
            if (attr.name === 'src') return;
            executedScript.setAttribute(attr.name, attr.value);
          });
          executedScript.setAttribute('data-storeeel-executed', 'true');

          const src = originalScript.getAttribute('src');
          if (src) {
            executedScript.setAttribute('src', src);
          } else {
            executedScript.textContent = originalScript.textContent;
          }
          originalScript.replaceWith(executedScript);
        });
      });
    };

    const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const adaptCustomHtmlStyleForShadowRoot = (cssText, campaignId) => {
      const escapedCampaignId = escapeRegExp(campaignId);
      const campaignRootSelector = [
        `div#storeeel-campaign-${escapedCampaignId}`,
        `#storeeel-campaign-${escapedCampaignId}`,
        `\\[data-storeeel-campaign-id=(["'])?${escapedCampaignId}\\1\\]`,
      ].join('|');

      const campaignRootRe = `(?:${campaignRootSelector})`;
      return cssText
        .replace(new RegExp(`${campaignRootRe}\\s*>\\s*`, 'g'), ':host .storeeel-shadow-root > ')
        .replace(new RegExp(`${campaignRootRe}\\s+`, 'g'), ':host .storeeel-shadow-root ')
        .replace(new RegExp(`${campaignRootRe}(?=\\s*(?:[,{]))`, 'g'), ':host .storeeel-shadow-root');
    };

    const mirrorCustomHtmlStylesToShadowRoot = (customHtmlContainer, shadowRoot, campaignId) => {
      if (!customHtmlContainer || !shadowRoot) return;

      customHtmlContainer.querySelectorAll('style, link[rel="stylesheet"]').forEach((styleNode) => {
        const mirroredStyleNode = styleNode.cloneNode(true);
        // 既存カスタムHTMLの本体向けCSSをShadow DOM化後も効かせる
        mirroredStyleNode.dataset.storeeelCustomHtmlMirrored = 'true';
        if (mirroredStyleNode.tagName.toLowerCase() === 'style') {
          mirroredStyleNode.textContent = adaptCustomHtmlStyleForShadowRoot(styleNode.textContent, campaignId);
        }
        shadowRoot.appendChild(mirroredStyleNode);
      });
    };

    const tenantId = window.storeeelTenantId || 'default';
    const currentUrl = window.storeeelCurrentUrl || normalizeCurrentUrl();
    const storedSegments = localStorageRepositories.get('targetingSegment')?.get?.(tenantId) || {};
    const globalState = getGlobalInitState();
    const displayOpportunityTrackedCampaignIds = globalState.displayOpportunityTrackedCampaignIds;
    const viewTrackedCampaignIds = globalState.viewTrackedCampaignIds;

    const renderCampaign = async (item) => {
      const savedAnswers = restoreTargetingAnswersFromStorage(item, {
        segments: storedSegments,
      });
      const displayItem = prepareCampaignForDisplay(item, savedAnswers, {
        tenantId,
        currentUrl,
      });
      if (!Array.isArray(displayItem?.contentGroups) || displayItem.contentGroups.length === 0) return;

      // カスタムHTMLをストーリー/リール本体の前後どちらに挿入するかを、Light DOM/Shadow DOM双方の分岐から参照する
      const customHtmlPosition = getCampaignCustomHtmlPosition(displayItem);
      const isCustomHtmlPositionBefore = customHtmlPosition === CUSTOM_HTML_POSITION.before;

      // すでに同一キャンペーンが挿入済みならスキップ（history復帰時の二重挿入防止）
      if (document.querySelector(`[data-storeeel-campaign-id="${item.id}"]`)) return;

      // preparedキャッシュの更新は「実際に描画する」ことが確定してから行う。
      // 重複ガードより前で上書きすると、DOMは1回目の描画のまま・キャッシュだけ新しい状態になり、
      // クリック時にグループIDが一致せず campaignIconsTapAction が黙って終了する。
      rememberPreparedCampaignForDisplay(item, displayItem, savedAnswers);

      const shadowDomEnabled = isStoreeelShadowDomEnabled(this.tagParams);

      const createHtmlContent = () => {
        const div = document.createElement('div');
        // キャンペーン挿入ルートをCSS/JSから特定しやすくする
        div.id = `storeeel-campaign-${item.id}`;
        div.className = 'storeeel-campaign-container';
        // 再表示制御の判定・二重挿入防止に使う
        div.dataset.storeeelCampaignId = String(item.id);
        // flex/grid 配下に outer_after で差し込まれても contain により幅が 0 へ潰れないようにする
        div.style.width = '100%';
        div.style.maxWidth = '100%';
        div.style.boxSizing = 'border-box';
        if (!item.isMobile) {
          // PCのみ中央寄せ
          div.style.justifyContent = 'center';
          div.style.display = 'flex';
        }
        div.style.contain = 'inline-size';
        const reelBase = document.createElement('div');
        reelBase.className = `storeeell_base_${item.id}`;
        reelBase.innerHTML = icons(displayItem);
        // PCでは auto_scroll_mode の有無に関係なく手動ドラッグを有効にする。
        // ただしタイル型は縦積み（flex-direction: column）で overflow-x を持たず横スクロールしないため、
        // ドラッグの利得がゼロな一方、5px動かすと直後のclickをcaptureで握り潰す副作用だけが乗る
        // （タイルを押しても無反応になる）。タイル型では有効化しない。
        if (displayItem?.frame !== 'tile') enableMouseDragScroll(reelBase);

        const style = document.createElement('style');
        // タイル型は管理画面の行・列に合わせ、tileRow ごとの行を縦に積む column flex レイアウト
        if (item.frame === 'tile') {
          // 行間 gap は tile_gap_vertical 専用ヘルパーで算出する（未設定時は既定 10px）
          const groupGapForTile = getCampaignTileGapVertical(displayItem);
          // グリッドの自然幅（列数×最大タイル幅+gap）でmax-widthを頭打ちし、
          // 表示領域より狭い場合は外側コンテナのPC中央寄せ/モバイル左寄せを効かせる
          const tileBlockMaxWidth = getCampaignTileBlockMaxWidth(displayItem);
          style.textContent = `
            .storeeell_base_${item.id} {
              display: flex;
              flex-direction: column;
              gap: ${groupGapForTile}px;
              box-sizing: border-box;
              /* タイル幅は行幅基準で算出するため、行コンテナ自身は親幅いっぱいまで広がれる必要がある一方、
                 グリッドの自然幅を超えて間延びしないよう上限も設ける */
              width: 100%;
              max-width: min(100%, ${tileBlockMaxWidth}px);
              padding: ${item.topPadding}px ${item.rightPadding}px ${item.bottomPadding}px ${item.leftPadding}px;
              margin: ${item.topMargin}px ${item.rightMargin}px ${item.bottomMargin}px ${item.leftMargin}px;
            }
          `;
        } else {
          style.textContent = `
            .storeeell_base_${item.id} {
              display: flex;
              align-items: ${item.frame === 'circle' ? 'flex-start' : 'center'};
              justify-content: left;
              /* 太い外枠でもスクロール領域自体は親幅内に収める */
              box-sizing: border-box;
              max-width: 100%;
              min-width: 0;
              overflow-x: auto;
              /* overflow-x だけ指定すると縦も auto になり、グループ名の文字が下端を
                 わずかにはみ出すだけで縦スクロール領域になる。Android Chrome では縦スワイプを
                 ここで受け止めてページがスクロールせず、オーバースクロール演出だけが出る */
              overflow-y: hidden;
              white-space: nowrap;
              padding: ${item.topPadding}px ${item.rightPadding}px ${item.bottomPadding}px ${item.leftPadding}px;
              margin: ${item.topMargin}px ${item.rightMargin}px ${item.bottomMargin}px ${item.leftMargin}px;
            }
            .storeeell_base_${item.id}::-webkit-scrollbar {
              display: none;
            }
          `;
        }

        div.appendChild(reelBase);
        const customHtml = displayItem.customHtml || displayItem.custom_html;
        if (customHtml) {
          const customHtmlContainer = document.createElement('div');
          customHtmlContainer.className = 'storeeel-campaign-custom-html';
          customHtmlContainer.innerHTML = customHtml;
          // 設定に応じてreelBaseの前/後どちらに差し込むかを切り替える（デフォルトは既存互換のafter）
          if (isCustomHtmlPositionBefore) {
            div.insertBefore(customHtmlContainer, reelBase);
          } else {
            div.appendChild(customHtmlContainer);
          }
        }
        div.appendChild(style);
        // タイル型はgridのgapで統一するため、後付けgap設定をスキップする
        if (item.frame !== 'tile') {
          const groupGap = getCampaignGroupGap(displayItem);
          if (groupGap != null) {
            // 閉じた状態のグループ間隔だけを変更し、カスタムHTMLには影響させない
            reelBase.style.gap = `${getRenderedCampaignGroupGap(displayItem)}px`;
          } else if (displayItem.frame === "circle") {
            reelBase.style.gap = '8px';
          }
        }
        return div;
      };

      const createShadowHtmlContent = () => {
        const host = createStoreeelCampaignHost(item, { center: !item.isMobile });
        const { shadowRoot, container } = createStoreeelShadowRootContainer(host, {
          scope: 'campaign',
          center: !item.isMobile,
        });

        const reelBase = document.createElement('div');
        reelBase.className = `storeeell_base_${item.id}`;
        reelBase.innerHTML = icons(displayItem);
        // PCでは auto_scroll_mode の有無に関係なく手動ドラッグを有効にする。
        // ただしタイル型は縦積み（flex-direction: column）で overflow-x を持たず横スクロールしないため、
        // ドラッグの利得がゼロな一方、5px動かすと直後のclickをcaptureで握り潰す副作用だけが乗る
        // （タイルを押しても無反応になる）。タイル型では有効化しない。
        if (displayItem?.frame !== 'tile') enableMouseDragScroll(reelBase);

        const style = document.createElement('style');
        style.dataset.storeeelShadowStyle = 'campaign';
        // タイル型は管理画面の行・列に合わせ、tileRow ごとの行を縦に積む column flex レイアウト（Shadow DOM版）
        if (item.frame === 'tile') {
          // 行間 gap は tile_gap_vertical 専用ヘルパーで算出する（未設定時は既定 10px）
          const groupGapForTile = getCampaignTileGapVertical(displayItem);
          // グリッドの自然幅（列数×最大タイル幅+gap）でmax-widthを頭打ちし、
          // 表示領域より狭い場合は外側コンテナのPC中央寄せ/モバイル左寄せを効かせる
          const tileBlockMaxWidth = getCampaignTileBlockMaxWidth(displayItem);
          style.textContent = `
            .storeeell_base_${item.id} {
              display: flex;
              flex-direction: column;
              gap: ${groupGapForTile}px;
              box-sizing: border-box;
              /* タイル幅は行幅基準で算出するため、行コンテナ自身は親幅いっぱいまで広がれる必要がある一方、
                 グリッドの自然幅を超えて間延びしないよう上限も設ける */
              width: 100%;
              max-width: min(100%, ${tileBlockMaxWidth}px);
              padding: ${item.topPadding}px ${item.rightPadding}px ${item.bottomPadding}px ${item.leftPadding}px;
              margin: ${item.topMargin}px ${item.rightMargin}px ${item.bottomMargin}px ${item.leftMargin}px;
            }
          `;
        } else {
          style.textContent = `
            .storeeell_base_${item.id} {
              display: flex;
              align-items: ${item.frame === 'circle' ? 'flex-start' : 'center'};
              justify-content: left;
              /* 太い外枠でもスクロール領域自体は親幅内に収める */
              box-sizing: border-box;
              max-width: 100%;
              min-width: 0;
              overflow-x: auto;
              /* overflow-x だけ指定すると縦も auto になり、グループ名の文字が下端を
                 わずかにはみ出すだけで縦スクロール領域になる。Android Chrome では縦スワイプを
                 ここで受け止めてページがスクロールせず、オーバースクロール演出だけが出る */
              overflow-y: hidden;
              white-space: nowrap;
              padding: ${item.topPadding}px ${item.rightPadding}px ${item.bottomPadding}px ${item.leftPadding}px;
              margin: ${item.topMargin}px ${item.rightMargin}px ${item.bottomMargin}px ${item.leftMargin}px;
            }
            .storeeell_base_${item.id}::-webkit-scrollbar {
              display: none;
            }
          `;
        }

        container.appendChild(style);
        container.appendChild(reelBase);

        const customHtml = displayItem.customHtml || displayItem.custom_html;
        const customHtmlContainer = customHtml ? document.createElement('div') : null;
        if (customHtmlContainer) {
          customHtmlContainer.className = 'storeeel-campaign-custom-html';
          customHtmlContainer.innerHTML = customHtml;
          mirrorCustomHtmlStylesToShadowRoot(customHtmlContainer, shadowRoot, item.id);
        }

        // タイル型はgridのgapで統一するため、後付けgap設定をスキップする
        if (item.frame !== 'tile') {
          const groupGap = getCampaignGroupGap(displayItem);
          if (groupGap != null) {
            // 閉じた状態のグループ間隔だけを変更し、カスタムHTMLには影響させない
            reelBase.style.gap = `${getRenderedCampaignGroupGap(displayItem)}px`;
          } else if (displayItem.frame === "circle") {
            reelBase.style.gap = '8px';
          }
        }

        return {
          element: host,
          shadowRoot,
          customHtmlContainer,
          getScrollContainer: () => reelBase,
          // 複数キャンペーンを同一アンカーへ順に挿入する際の基準はDOM上最後尾の要素にする
          // （before: customHtmlContainerがhostより前に来るためhostが最後尾、after: 従来通りcustomHtmlContainerが最後尾）
          getInsertedAnchor: () => (isCustomHtmlPositionBefore ? host : (customHtmlContainer || host)),
        };
      };

      // 動画を挿入する要素を取得
      const siteTitle = await findSiteTitleWithRetry(item);
      if (!siteTitle) {
        console.warn('[StoreeelLibrary] 挿入先要素が見つかりませんでした:', {
          insertElementKind: insertSelection?.insertElementKind ?? item.insertElementKind,
          insertElementValue: insertSelection?.insertElementValue ?? item.insertElementValue,
          insertElementIndex: insertSelection?.insertElementIndex ?? item.insertElementIndex
        });
        return;
      }
      // 動画を挿入(位置指定)
      const htmlContent = shadowDomEnabled
        ? createShadowHtmlContent()
        : {
          element: createHtmlContent(),
          shadowRoot: null,
          customHtmlContainer: null,
          getScrollContainer: null,
          getInsertedAnchor: null,
        };
      const htmlElement = htmlContent.element;
      let inserted = false;
      const insertPosition = insertSelection?.position ?? item.position;
      const canInsertAtPosition = (
        insertPosition === 'inner_start' ||
        insertPosition === 'inner_end' ||
        ((insertPosition === 'outer_before' || insertPosition === 'outer_after') && siteTitle.parentNode)
      );
      switch (insertPosition) {
        case "inner_start":
          siteTitle.insertBefore(htmlElement, siteTitle.firstChild);
          inserted = true;
          break;
        case "inner_end":
          siteTitle.appendChild(htmlElement);
          inserted = true;
          break;
        case "outer_before":
          siteTitle.parentNode.insertBefore(htmlElement, siteTitle);
          inserted = true;
          break;
        case "outer_after":
          siteTitle.parentNode.insertBefore(htmlElement, siteTitle.nextSibling);
          inserted = true;
          break;
      }
      if (inserted && htmlContent.customHtmlContainer) {
        // 設定に応じてホスト要素の前/後どちらに差し込むかを切り替える（デフォルトは既存互換のafterend）
        htmlElement.insertAdjacentElement(
          isCustomHtmlPositionBefore ? 'beforebegin' : 'afterend',
          htmlContent.customHtmlContainer
        );
      }
      if (!inserted) return;
      if (track && canInsertAtPosition) {
        trackCampaignDisplayOpportunity(item, displayOpportunityTrackedCampaignIds);
      }
      if (insertSelection && insertPosition === 'outer_after') {
        // 同一アンカーへ複数件入れる時に表示順が逆転しないよう、直前の挿入要素を次の基準にする
        selectedInsertAnchor = htmlContent.getInsertedAnchor?.() || htmlElement;
      }

      if (track) {
        trackCampaignViewWhenVisible(htmlElement, item, viewTrackedCampaignIds);
      }

      // auto_scroll_mode に応じて、キャンペーンの横方向自動スクロールを制御
      const autoScrollMode = getCampaignAutoScrollMode(item);
      if (autoScrollMode !== AUTO_SCROLL_MODE.disabled) {
        const scrollContainer = htmlContent.getScrollContainer?.()
          || htmlElement.querySelector(`.storeeell_base_${item.id}`);
        if (!scrollContainer) {
          console.warn(`[StoreeelLibrary] .storeeell_base_${item.id} が見つかりませんでした`);
          return;
        }
        enableCampaignAutoScroll(scrollContainer, {
          // PC の auto scroll 有効時は常に無限ループにする
          loop: item.isMobile ? autoScrollMode === AUTO_SCROLL_MODE.continuous : true,
          // iPhone のみ transform ベースに切り替え、scrollLeft 更新由来のカクつきを抑える
          renderMode: item.isMobile && window.isIphone ? 'transform' : 'scroll',
        });
      } else {
        const scrollContainer = htmlContent.getScrollContainer?.()
          || htmlElement.querySelector(`.storeeell_base_${item.id}`);
        if (!scrollContainer) {
          console.warn(`[StoreeelLibrary] .storeeell_base_${item.id} が見つかりませんでした`);
          return;
        }
      }

      const customHtmlRoots = [
        ...Array.from(htmlElement.querySelectorAll('.storeeel-campaign-custom-html')),
        htmlContent.customHtmlContainer,
      ].filter(Boolean);
      customHtmlRoots.forEach((customHtmlRoot) => {
        executeScriptsInCustomHtml(customHtmlRoot);
      });

      // 挿入できたキャンペーンは、他キャンペーンの探索完了を待たずに操作可能にする
      setupVideoIntersectionObserver(htmlContent.shadowRoot);
      campaignIconsTapAction(htmlContent.shadowRoot);
      tryOpenSharedContent();
    };

    if (insertSelection) {
      for (const item of items.data) {
        // selector モードでは outer_after の順序保証を優先して逐次描画する
        await renderCampaign(item);
      }
    } else {
      await Promise.all(items.data.map((item) => renderCampaign(item)));
    }

    setupVideoIntersectionObserver();
    campaignIconsTapAction();
    tryOpenSharedContent();

    // 初期描画とカスタムHTMLの処理がすべて終わったあとにだけDOM監視を開始する
    this._setupAutoReexecOnDomMutations();
  }
}


window.StoreeelLibrary = reelLibrary
